import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Filter, X, ChevronDown, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import SearchBar from '../components/SearchBar';
import ProductCard from '../components/ProductCard';
import SEOHead from '../components/SEO/SEOHead';

const API = process.env.REACT_APP_BACKEND_URL;

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const collectionFromUrl = searchParams.get('collection') || '';
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ supplier: null, collection: null });

  // Load available filters
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await axios.get(`${API}/api/search/filters`);
        setFilters(response.data);
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  // Search when query or filters change
  useEffect(() => {
    const search = async () => {
      if (!query) {
        setResults([]);
        setTotalCount(0);
        return;
      }

      setLoading(true);
      try {
        const params = { q: query, limit: 10 };
        if (selectedSupplier) {
          params.supplier = selectedSupplier;
        }
        if (collectionFromUrl) {
          params.collection = collectionFromUrl;
        }
        
        const response = await axios.get(`${API}/api/search`, { params });
        setResults(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setAppliedFilters({
          supplier: response.data.supplier_filter,
          collection: response.data.collection_filter
        });
      } catch (error) {
        console.error('Error searching:', error);
        setResults([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    search();
  }, [query, selectedSupplier, collectionFromUrl]);

  const handleSearch = (newQuery) => {
    // Conserver la collection si elle existe
    let url = `/recherche?q=${encodeURIComponent(newQuery)}`;
    if (collectionFromUrl) {
      url += `&collection=${encodeURIComponent(collectionFromUrl)}`;
    }
    navigate(url);
  };

  const clearFilters = () => {
    setSelectedSupplier('');
  };

  const activeFiltersCount = [selectedSupplier].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Head pour la page de recherche */}
      <SEOHead 
        title={query ? `Recherche: ${query}` : 'Recherche de toiles'}
        description={query ? `Résultats de recherche pour "${query}" - Trouvez la toile de store banne parfaite parmi notre large sélection.` : 'Recherchez parmi notre large gamme de toiles pour stores bannes sur mesure.'}
        url={`/recherche?q=${query}`}
        noindex={true}
      />
      
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            
            <div className="flex-1">
              <SearchBar 
                onSearch={handleSearch}
                placeholder="Rechercher une toile, une référence, une couleur..."
                showButton={false}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Results header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            {query && (
              <>
                <h1 className="text-2xl font-bold text-gray-900">
                  {loading ? 'Recherche...' : (
                    <>
                      {totalCount} résultat{totalCount !== 1 ? 's' : ''} pour "{query}"
                    </>
                  )}
                </h1>
                {/* Afficher les filtres appliqués */}
                {(appliedFilters.supplier || appliedFilters.collection) && !loading && (
                  <p className="text-sm text-gray-500 mt-1">
                    Filtré par : 
                    {appliedFilters.collection && <span className="font-medium text-celadon ml-1">{appliedFilters.collection}</span>}
                    {appliedFilters.collection && appliedFilters.supplier && <span> • </span>}
                    {appliedFilters.supplier && <span className="font-medium text-celadon">{appliedFilters.supplier}</span>}
                  </p>
                )}
              </>
            )}
          </div>
          
          {/* Filter button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              activeFiltersCount > 0 
                ? 'border-celadon bg-celadon/10 text-celadon' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Filter size={18} />
            <span>Filtres</span>
            {activeFiltersCount > 0 && (
              <span className="bg-celadon text-white text-xs px-2 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
            <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters panel */}
        {showFilters && filters && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Filtrer par</h3>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-celadon hover:underline flex items-center gap-1"
                >
                  <X size={14} />
                  Effacer les filtres
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Supplier filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fournisseur
                </label>
                <select
                  value={selectedSupplier}
                  onChange={(e) => setSelectedSupplier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-celadon focus:border-celadon"
                >
                  <option value="">Tous les fournisseurs</option>
                  {filters.suppliers.map(supplier => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Active filters tags */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedSupplier && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-celadon/10 text-celadon rounded-full text-sm">
                {selectedSupplier}
                <button onClick={() => setSelectedSupplier('')} className="hover:text-celadon-dark">
                  <X size={14} />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-celadon border-t-transparent"></div>
          </div>
        ) : results.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {results.map(product => (
              <ProductCard 
                key={product.id} 
                product={product}
                showPrice={true}
              />
            ))}
          </div>
        ) : query ? (
          <div className="text-center py-20">
            <Search className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun résultat trouvé
            </h2>
            <p className="text-gray-500 mb-6">
              Aucun produit ne correspond à "{query}"
            </p>
            <div className="text-sm text-gray-500">
              <p className="mb-2">Suggestions :</p>
              <ul className="list-disc list-inside">
                <li>Vérifiez l'orthographe des termes de recherche</li>
                <li>Essayez des termes plus généraux</li>
                <li>Essayez avec moins de mots</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <Search className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Recherchez une toile
            </h2>
            <p className="text-gray-500">
              Entrez une référence, un nom, une couleur ou un fournisseur
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
